import authRoutes from '../router/mainRoutes/authRoutes';
import contentRoutes from '../router/mainRoutes/contentRoutes';
import {drawerRoutes} from '../router/mainRoutes/drawerRoutes';
import onboardingRoutes from '../router/mainRoutes/onboardingRoutes';
import {StackItem} from '../interfaces/router/stackItem';
import {DrawerItem} from '../interfaces/router/drawerItem';
import screens from '../config/screens';

// Helper function to convert screen name to URL-friendly path
const getWebPathFromScreenName = (screenName: string): string => {
  // Remove any special characters and convert to lowercase kebab-case
  return screenName
    .replace(/[^\w\s]/gi, '')
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '')
    .replace(/\s+/g, '-');
};

// Function to generate all web paths
const generateWebPaths = () => {
  const webPaths: Record<string, string> = {};

  // Process simple stack routes - use clean, simple paths
  const processStackRoutes = (routes: StackItem[], prefix = '') => {
    routes.forEach(route => {
      // Create simplified path names
      const simplePath = getWebPathFromScreenName(route.name);
      webPaths[route.name] = prefix + simplePath;
    });
  };

  // Process drawer routes with shorter paths
  const processDrawerRoutes = (routes: DrawerItem[], prefix = '') => {
    routes.forEach(route => {
      // For drawer routes, just use the simple path name without additional prefixes
      webPaths[route.name] = prefix + getWebPathFromScreenName(route.name);
    });
  };

  // Create simplified mappings for bottom tabs
  const mapTabScreens = () => {
    // Main sections - unique URLs for each navigation component
    webPaths[screens.homeDrawer] = 'dashboard';
    webPaths[screens.homeTabs] = 'tabs';
    webPaths[screens.content.homeTab] = 'home-tab';
    webPaths[screens.content.home] = 'home';

    // Other main content sections with simple paths
    webPaths[screens.content.filterTabNews] = 'filter-tab-news';
    webPaths[screens.content.medicalUpdatesCongresses] =
      'medical-updates-congresses';
    webPaths[screens.content.tabNewsResults] = 'tab-news-results';
    webPaths[screens.content.tabNews] = 'tab-news';
    webPaths[screens.content.latest] = 'latest';
    webPaths[screens.content.drugs] = 'drugs';
    webPaths[screens.content.drugsDiseases] = 'drugs-diseases';
    webPaths[screens.content.drugsDiseasesDetails] = 'drugs-diseases-details';
    webPaths[screens.content.medicalUpdates] = 'updates';
    webPaths[screens.content.genioChat] = 'chat';
    webPaths[screens.content.enterClinicalCase] = 'case/new';
    webPaths[screens.content.clinicalCase] = 'case';

    // Add sharedArticle and sharedChat screen mappings
    webPaths[screens.sharedArticle] = 'shared-article';
    webPaths[screens.sharedChat] = 'shared/chats/:chatId';

    // Profile and settings - direct paths
    webPaths[screens.content.profile] = 'profile';
    webPaths[screens.content.settings] = 'settings';

    // Other common screens
    webPaths[screens.content.search] = 'search';
    webPaths[screens.content.fileViewer] = 'view';
    webPaths[screens.content.addNote] = 'notes/new';
    webPaths[screens.content.viewNote] = 'notes/view';
    webPaths[screens.content.notes] = 'notes';
    webPaths[screens.content.notesRoutes] = 'notes-section';

    // Additional screen mappings to simplify paths
    webPaths[screens.content.forYou] = 'for-you';
    webPaths[screens.content.favorite] = 'favorites';
    webPaths[screens.content.favoriteRoutes] = 'favorites-section';
    webPaths[screens.content.library] = 'library';
    webPaths[screens.content.topics] = 'topics';
    webPaths[screens.content.documents] = 'documents';
    webPaths[screens.content.news] = 'news';
    webPaths[screens.content.notifications] = 'notifications';
    webPaths[screens.content.article] = 'article';
    webPaths[screens.content.latestArticle] = 'article/latest';
    webPaths[screens.content.chooseYourSpeciality] = 'speciality';

    webPaths[screens.content.patients] = 'patients';
    webPaths[screens.content.viewPatient] = 'view-patient';
    webPaths[screens.content.addEditPatient] = 'update-patient';
    webPaths[screens.content.addEditPatientReport] = 'update-report';
    webPaths[screens.content.viewPatientReport] = 'view-report';

    webPaths[screens.content.privacyPolicy] = 'privacy';
    webPaths[screens.content.termsConditions] = 'terms';
    webPaths[screens.content.changePassword] = 'password';
    webPaths[screens.content.fontSize] = 'font-size';
    webPaths[screens.content.darkMode] = 'dark-mode';
    webPaths[screens.content.customerSupport] = 'support';
    webPaths[screens.content.changeLanguage] = 'language';
    webPaths[screens.content.filters] = 'filters';
    webPaths[screens.content.calculators] = 'calculators';
    webPaths[screens.content.voiceInteraction] = 'voice';
    webPaths[screens.content.summaries] = 'summaries';
    webPaths[screens.content.afterLogin] = 'welcome';
    webPaths[screens.content.drugsDetails] = 'drugs/details';
    webPaths[screens.content.customizeHome] = 'customize';
    webPaths[screens.content.genioChatRoutes] = 'chats';
    webPaths[screens.content.genioChat] = 'chat';
  };

  // 1. Auth routes (login paths)
  processStackRoutes(authRoutes, 'auth/');

  // 2. Onboarding routes - keep at root level
  processStackRoutes(onboardingRoutes, '');

  // 3. Content routes (main app screens) - simplified paths
  processStackRoutes(contentRoutes, '');

  // 4. Drawer routes - simplified
  processDrawerRoutes(drawerRoutes, '');

  // 5. Apply custom mapping for tabs and frequently used screens
  mapTabScreens();

  return webPaths;
};

// Create the web-specific navigation config
const navigationConfigWeb = {
  // Configuration for @react-navigation/native-stack
  screens: generateWebPaths(),

  // Additional web-specific configuration
  config: {
    path: '/',

    // Ensure all paths are correctly preserved
    initialRouteName: undefined,
  },
};

export default navigationConfigWeb;
